import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { SystemStyleObject } from "@chakra-ui/react";
import colors from "app/colors";
import { CreatableSelect, MultiValue } from "chakra-react-select";
import { IIsNew } from "gql/isNew";
import { isEmpty, uniqBy } from "lodash";
import { UserAttributeFragment } from "gql/graphql";
import { useUserAttributes } from "modules/providers/user-attributes-provider";

interface IProps {
  isDisabled?: boolean;
  userAttributeCategoryId?: string;
  onChange: (value: readonly INewUserAttributeOption[]) => void;
}

export interface INewUserAttributeOption extends IIsNew {
  readonly label: string;
  readonly value: string;
  readonly duplicateUserAttribute?: UserAttributeFragment;
}

const AdminCreateUserAttributeSelect = ({
  isDisabled = false,
  userAttributeCategoryId,
  onChange,
}: IProps) => {
  const { userAttributesByUserAttributeCategoryId } = useUserAttributes();
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<readonly INewUserAttributeOption[]>([]);

  const _userAttributes = useMemo(
    () =>
      userAttributeCategoryId
        ? userAttributesByUserAttributeCategoryId[userAttributeCategoryId]
        : [],
    [userAttributeCategoryId, userAttributesByUserAttributeCategoryId],
  );

  const getDuplicateUserAttribute = useCallback(
    (value: string) => {
      const _value = value.toLowerCase();
      return _userAttributes.find(
        (userAttribute) => userAttribute.name.toLowerCase() === _value,
      );
    },
    [_userAttributes],
  );

  const _onChange = useCallback(
    (options: MultiValue<INewUserAttributeOption>) => {
      const _newOptions = uniqBy(options, "value");
      setValue(_newOptions);
      onChange(_newOptions);
    },
    [onChange],
  );

  useEffect(() => {
    _onChange(
      value.map((v) => ({
        ...v,
        duplicateUserAttribute: getDuplicateUserAttribute(v.value),
      })),
    );
  }, [_onChange, getDuplicateUserAttribute, value]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!inputValue || isEmpty(inputValue)) return;
      switch (e.key) {
        case "Enter":
        case "Tab":
          _onChange([
            ...value,
            {
              label: inputValue,
              value: inputValue,
              duplicateUserAttribute: getDuplicateUserAttribute(inputValue),
              __isNew__: true,
            },
          ]);
          setInputValue("");
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [inputValue, _onChange, value, getDuplicateUserAttribute],
  );

  const onBlur = useCallback(() => {
    if (!inputValue || isEmpty(inputValue)) return;
    _onChange([
      ...value,
      {
        label: inputValue,
        value: inputValue,
        duplicateUserAttribute: getDuplicateUserAttribute(inputValue),
        __isNew__: true,
      },
    ]);
  }, [_onChange, getDuplicateUserAttribute, inputValue, value]);

  const _onInputChange = useCallback(
    (newValue: string) => {
      if (newValue.includes(",")) {
        _onChange([
          ...value,
          ...newValue
            .split(",")
            .map((v) => v.toLowerCase().trim())
            .filter((v) => v.length > 0 && !isEmpty(v))
            .map((v) => ({
              label: v,
              value: v,
              duplicateUserAttribute: getDuplicateUserAttribute(v),
              __isNew__: true,
            })),
        ]);
        setInputValue("");
        return;
      }
      setInputValue(newValue);
    },
    [_onChange, getDuplicateUserAttribute, value],
  );

  const formatOptionLabel = useCallback(
    (option: INewUserAttributeOption) => option.label,
    [],
  );

  return (
    <CreatableSelect
      isDisabled={isDisabled}
      isMulti
      isClearable
      menuIsOpen={false}
      inputValue={inputValue}
      onInputChange={_onInputChange}
      onKeyDown={onKeyDown}
      onChange={_onChange}
      formatOptionLabel={formatOptionLabel}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      onBlur={onBlur}
      value={value}
      placeholder="Add values for this category"
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          fontSize: 14,
          color: colors.placeholderText,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "100%",
        }),
        multiValue: (
          provided: SystemStyleObject,
          state,
        ): SystemStyleObject => ({
          ...provided,
          borderRadius: "full",
          borderWidth: "1px",
          borderColor: state.data.duplicateUserAttribute
            ? colors.red
            : colors.blue,
          backgroundColor: state.data.duplicateUserAttribute
            ? colors.redLight
            : colors.white,
          color: state.data.duplicateUserAttribute ? colors.red : colors.blue,
          fontSize: 14,
        }),
      }}
    />
  );
};

export default memo(AdminCreateUserAttributeSelect);
